import { StyleSheet, Platform } from 'react-native'
import { B2B_MEMBERS_TABLE_SCROLL_MAX_HEIGHT } from '../../constants/accountAdmin.constants'
import type { Theme } from '../../../theme/getTheme'

/** Web: padding + width % default to content-box and exceed 100% row width; border-box fixes overlap. */
const webTableCellBox =
  Platform.OS === 'web' ? ({ boxSizing: 'border-box' as never } as const) : {}

export function getAccountMembersTabStyles(theme: Theme, isNarrow = false) {
  return StyleSheet.create({
    scrollView: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    /** Plain column when site `/account-admin` uses outer ScrollView (web). */
    embeddedBody: {
      width: '100%',
      alignSelf: 'stretch',
    },
    scrollContentCore: {
      width: '100%',
      ...(Platform.OS === 'web' && {
        alignSelf: 'stretch' as never,
      }),
    },
    scrollContentBottomPad: {
      paddingBottom: theme.spacing.huge * 2,
    },
    /** Space between major blocks (join code, invitation link, members list). */
    majorBlock: {
      marginBottom: theme.spacing.huge,
      width: '100%',
      alignSelf: 'stretch',
    },
    /** Last block before the member directory — no bottom margin; divider handles separation. */
    majorBlockBeforeDirectory: {
      marginBottom: 0,
    },
    sectionDivider: {
      height: StyleSheet.hairlineWidth,
      width: '100%',
      alignSelf: 'stretch',
      backgroundColor: theme.gray[200],
      marginTop: theme.spacing.lg,
      marginBottom: theme.spacing.huge,
    },
    addMembersIntro: {
      fontSize: 14,
      color: theme.gray[700],
      marginBottom: theme.spacing.lg,
      lineHeight: 22,
    },
    membersSection: {
      marginBottom: theme.spacing.lg,
      width: '100%',
      alignSelf: 'stretch',
    },
    directoryHint: {
      fontSize: 13,
      color: theme.gray[600],
      marginBottom: theme.spacing.md,
      lineHeight: 20,
      width: '100%',
      alignSelf: 'stretch',
      flexShrink: 1,
    },
    tableOuter: {
      width: '100%',
      alignSelf: 'stretch',
    },
    /** Full width of org column; rounded border via overflow hidden (no maxWidth — gap + % cols used to clip ACTIONS). */
    tableFrame: {
      width: '100%',
      alignSelf: 'stretch',
      borderWidth: 1,
      borderColor: theme.gray[200],
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: isNarrow ? theme.gray[100] : theme.background.card,
    },
    /** Scrollable body beneath the sticky header — caps height so rows scroll internally. */
    tableScrollBody: {
      maxHeight: B2B_MEMBERS_TABLE_SCROLL_MAX_HEIGHT,
    },
    tableHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      width: '100%',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      backgroundColor: theme.background.cardElevated,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.gray[200],
      ...(isNarrow && { display: 'none' as never }),
    },
    tableHeaderCell: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.gray[700],
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      ...(Platform.OS === 'web' && {
        whiteSpace: 'nowrap' as never,
      }),
    },
    /**
     * Flex columns (not width %) + web border-box: percent + horizontal padding used to overflow the row
     * and clip under tableFrame overflow:hidden, misaligning headers vs cells and colliding ROLE/JOINED.
     */
    tableHeaderName: {
      flexGrow: 23,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      paddingRight: theme.spacing.sm,
      ...webTableCellBox,
    },
    tableHeaderEmail: {
      flexGrow: 24,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      paddingRight: theme.spacing.sm,
      ...webTableCellBox,
    },
    tableHeaderRole: {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 0,
      minWidth: 76,
      paddingRight: theme.spacing.sm,
      ...webTableCellBox,
    },
    tableHeaderJoined: {
      flexGrow: 13,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 56,
      paddingRight: theme.spacing.sm,
      ...webTableCellBox,
    },
    tableHeaderActionsWrap: {
      flexGrow: 23,
      flexShrink: 0,
      flexBasis: 0,
      minWidth: 0,
      alignItems: 'flex-end',
      ...webTableCellBox,
    },
    tableHeaderIndex: {
      flexGrow: 0,
      flexShrink: 0,
      width: 36,
      minWidth: 36,
      paddingRight: theme.spacing.xs,
      ...webTableCellBox,
    },
    tableColIndex: {
      flexGrow: 0,
      flexShrink: 0,
      width: 36,
      minWidth: 36,
      paddingRight: theme.spacing.xs,
      justifyContent: 'center',
      ...webTableCellBox,
    },
    tableIndexText: {
      fontSize: 12,
      color: theme.gray[500],
      fontVariant: ['tabular-nums'],
    },
    tableDataRow: {
      ...(isNarrow
        ? {
            flexDirection: 'column' as const,
            width: '100%',
            marginHorizontal: theme.spacing.sm,
            marginVertical: theme.spacing.sm,
            paddingHorizontal: theme.spacing.md,
            paddingVertical: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.gray[200],
            borderRadius: 12,
            backgroundColor: theme.background.cardElevated,
            ...(Platform.OS === 'web' && {
              boxShadow: `0 1px 3px rgba(0, 0, 0, 0.08)` as never,
            }),
          }
        : {
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            flexWrap: 'nowrap' as const,
            width: '100%',
            /** RN Web: avoid UA / transparent rows reading as white against dark shells. */
            backgroundColor: theme.background.card,
          }),
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.md,
      borderBottomWidth: isNarrow ? 0 : StyleSheet.hairlineWidth,
      borderBottomColor: theme.gray[200],
    },
    tableDataRowLast: {
      borderBottomWidth: 0,
    },
    mobileDivider: {
      width: '100%',
      height: StyleSheet.hairlineWidth,
      backgroundColor: theme.gray[200],
      marginTop: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
    },
    /** Narrow: header row (Name + Role). */
    narrowLine1: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      width: '100%',
    },
    /** Narrow: line 2 (Email). */
    narrowLineEmail: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      marginTop: theme.spacing.xs,
    },
    /** Narrow: footer row (Joined + Actions). */
    narrowLine2: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: theme.spacing.sm,
    },
    tableColName: {
      ...(isNarrow
        ? {
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            minWidth: 0,
          }
        : {
            flexGrow: 23,
            flexShrink: 1,
            flexBasis: 0,
            minWidth: 0,
            paddingRight: theme.spacing.sm,
          }),
      ...webTableCellBox,
    },
    tableColEmail: {
      ...(isNarrow
        ? {
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            minWidth: 0,
          }
        : {
            flexGrow: 24,
            flexShrink: 1,
            flexBasis: 0,
            minWidth: 0,
            paddingRight: theme.spacing.sm,
          }),
      ...webTableCellBox,
    },
    tableColRole: {
      ...(isNarrow
        ? {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: 'auto' as never,
            minWidth: 0,
            paddingRight: 0,
          }
        : {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: 0,
            minWidth: 76,
            paddingRight: theme.spacing.sm,
          }),
      justifyContent: 'center',
      ...webTableCellBox,
    },
    tableColJoined: {
      ...(isNarrow
        ? {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: 'auto' as never,
            minWidth: 0,
            paddingRight: theme.spacing.sm,
          }
        : {
            flexGrow: 13,
            flexShrink: 1,
            flexBasis: 0,
            minWidth: 56,
            paddingRight: theme.spacing.sm,
          }),
      ...webTableCellBox,
    },
    tableColActions: {
      flexGrow: isNarrow ? 0 : 23,
      flexShrink: 0,
      flexBasis: isNarrow ? 'auto' as never : 0,
      minWidth: isNarrow ? 0 : 0,
      flexDirection: 'row',
      flexWrap: isNarrow ? ('nowrap' as const) : ('wrap' as const),
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: theme.spacing.xs,
      ...webTableCellBox,
    },
    tableColActionsPending: {
      minHeight: 32,
    },
    tableMemberPrimary: {
      fontSize: 15,
      color: theme.gray[900],
      ...(Platform.OS === 'web' && {
        whiteSpace: 'nowrap' as never,
        overflow: 'hidden' as never,
        textOverflow: 'ellipsis' as never,
      }),
    },
    tableMemberPrimaryName: {
      fontWeight: '500',
    },
    tableMemberPrimaryYou: {
      fontWeight: '400',
      color: theme.gray[600],
    },
    tableEmailText: {
      fontSize: 13,
      color: theme.gray[700],
      ...(Platform.OS === 'web' && {
        whiteSpace: 'nowrap' as never,
        overflow: 'hidden' as never,
        textOverflow: 'ellipsis' as never,
      }),
    },
    tableJoinedText: {
      fontSize: 13,
      color: theme.gray[700],
      ...(Platform.OS === 'web' && {
        whiteSpace: 'nowrap' as never,
      }),
    },
    tableActionsPlaceholder: {
      fontSize: 13,
      fontStyle: 'italic',
      color: theme.gray[500],
    },
    roleBadge: {
      alignSelf: 'flex-start',
      paddingVertical: 4,
      paddingHorizontal: 10,
      borderRadius: 8,
    },
    roleBadgeAdmin: {
      backgroundColor: theme.primary + '22',
    },
    roleBadgeAdminText: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.primary,
    },
    roleBadgeMember: {
      backgroundColor: theme.gray[200],
    },
    roleBadgeMemberText: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.gray[700],
    },
    tableEmpty: {
      paddingVertical: theme.spacing.lg,
      paddingHorizontal: theme.spacing.md,
      fontSize: 14,
      color: theme.gray[600],
      textAlign: 'center',
    },
    title: {
      fontSize: 16,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: theme.spacing.md,
    },
    inviteTitle: {
      marginTop: theme.spacing.lg,
    },
    seatText: {
      fontSize: 14,
      color: theme.gray[700],
      marginBottom: theme.spacing.md,
    },
    inviteHint: {
      fontSize: 14,
      color: theme.gray[700],
      marginBottom: theme.spacing.sm,
    },
    inviteLinkText: {
      fontSize: 13,
      color: theme.primaryLight,
      textDecorationLine: 'underline',
      textDecorationColor: theme.primaryLight,
      marginBottom: theme.spacing.md,
      // Do not use flex:1 inside ScrollView — on web it skews content height and cuts off scroll range
      alignSelf: 'stretch',
      minWidth: 0,
    },
    inviteLinkActionsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
    },
    inviteQrHint: {
      fontSize: 13,
      color: theme.gray[600],
      marginBottom: theme.spacing.sm,
      lineHeight: 20,
    },
    inviteQrWrap: {
      alignSelf: 'flex-start',
      marginBottom: theme.spacing.md,
      padding: theme.spacing.sm,
      borderRadius: 12,
      backgroundColor: theme.background.cardElevated,
      borderWidth: 1,
      borderColor: theme.gray[400],
    },
    removeBtn: {
      flexShrink: 0,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: 8,
      backgroundColor: theme.error + '22',
      ...(isNarrow && {
        flexGrow: 0,
        flexBasis: 'auto' as never,
        alignItems: 'center' as never,
        justifyContent: 'center' as never,
      }),
    },
    removeBtnText: {
      color: theme.error,
      fontWeight: '700',
      fontSize: 13,
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.gray[300],
    },
    secondaryButtonText: {
      color: theme.primaryLight,
      fontWeight: '600',
    },
    secondaryButtonPressed: {
      opacity: 0.85,
    },
    secondaryButtonHover: {
      opacity: 0.92,
    },
    removeBtnPressed: {
      opacity: 0.85,
    },
    removeBtnDisabled: {
      opacity: 0.45,
    },
    makeAdminBtn: {
      flexShrink: 0,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: 8,
      backgroundColor: theme.primary + '18',
      borderWidth: 1,
      borderColor: theme.primary + '55',
      ...(isNarrow && {
        flexGrow: 0,
        flexBasis: 'auto' as never,
        alignItems: 'center' as never,
        justifyContent: 'center' as never,
      }),
    },
    makeAdminBtnText: {
      color: theme.primary,
      fontWeight: '700',
      fontSize: 13,
      ...(Platform.OS === 'web' && {
        whiteSpace: 'nowrap' as never,
      }),
    },
    makeAdminBtnPressed: {
      opacity: 0.85,
    },
    makeAdminBtnHover: {
      opacity: 0.92,
    },
    makeAdminBtnDisabled: {
      opacity: 0.45,
    },
    demoteBtn: {
      flexShrink: 0,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: 8,
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: theme.gray[300],
      ...(isNarrow && {
        flexGrow: 0,
        flexBasis: 'auto' as never,
        paddingVertical: Math.max(0, theme.spacing.xs - 1),
        alignItems: 'center' as never,
        justifyContent: 'center' as never,
      }),
    },
    demoteBtnText: {
      color: theme.black,
      fontWeight: '700',
      fontSize: 13,
    },
    demoteBtnPressed: {
      opacity: 0.85,
    },
    demoteBtnHover: {
      opacity: 0.92,
    },
    demoteBtnDisabled: {
      opacity: 0.45,
    },
    bulkToolbar: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginBottom: theme.spacing.md,
      width: '100%',
      alignSelf: 'stretch',
    },
    bulkToolbarBtn: {
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: 'transparent',
      marginRight: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
      flexDirection: 'row',
      alignItems: 'center',
    },
    bulkToolbarBtnPrimary: {
      borderColor: theme.primary,
      backgroundColor: theme.primary + '14',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    bulkToolbarBtnText: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.primaryLight,
    },
    bulkToolbarBtnTextPrimary: {
      color: theme.primary,
    },
    bulkToolbarBtnDisabled: {
      opacity: 0.45,
    },
    tableHeaderCheckbox: {
      flexGrow: 0,
      flexShrink: 0,
      width: 44,
      minWidth: 44,
      alignItems: 'center',
      justifyContent: 'center',
      ...webTableCellBox,
    },
    tableColCheckbox: {
      flexGrow: 0,
      flexShrink: 0,
      width: 44,
      minWidth: 44,
      alignItems: 'center',
      justifyContent: 'center',
      ...webTableCellBox,
    },
    /** Minimum tap target for row/header checkboxes (web nested scroll can miss tiny targets). */
    checkboxHitArea: {
      width: 44,
      height: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxOuter: {
      width: 22,
      height: 22,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: theme.gray[400],
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxOuterSelected: {
      borderColor: theme.primary,
      backgroundColor: theme.primary,
    },
    checkboxInnerDot: {
      width: 10,
      height: 10,
      borderRadius: 2,
      backgroundColor: theme.white,
    },
  })
}
